import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, User as UserIcon, Bot, Shield, CheckCircle2, LifeBuoy } from 'lucide-react';
import ReviewReferralCard from '@/components/ReviewReferralCard';
import { TicketBillingCard } from '@/components/TicketBillingCard';
import { apiFetch, toast } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions for styling
function getStatusClassName(status) {
  if (status === 'open') return 'bg-[#00f0ff]/20 text-[#00f0ff]';
  if (status === 'in_progress') return 'bg-[#ffa500]/20 text-[#ffa500]';
  if (status === 'resolved') return 'bg-[#00ff00]/20 text-[#00ff00]';
  if (status === 'closed') return 'bg-[#808080]/20 text-[#808080]';
  return 'bg-gray-500/20 text-gray-400';
}

function getPriorityClassName(priority) {
  if (priority === 'low') return 'text-[#00ff00]';
  if (priority === 'medium') return 'text-[#ffa500]';
  return 'text-[#ff0000]';
}

function getMessageClassName(senderType) {
  if (senderType === 'user') return 'chat-user';
  if (senderType === 'ai') return 'chat-ai';
  return 'chat-admin';
}

function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billing, setBilling] = useState(null);
  const [user, setUser] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [resolveFeedback, setResolveFeedback] = useState('');

  useEffect(() => {
    loadUser();
    loadTicketData();
    loadBilling();
    
    // Poll for new messages every 5 seconds only if ticket is open or in_progress
    const interval = setInterval(() => {
      if (ticket && (ticket.status === 'open' || ticket.status === 'in_progress')) {
        loadMessages();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [ticketId, ticket?.status]); // Re-run when ticketId or status changes

  const loadUser = async () => {
    try {
      const res = await apiFetch(`${API}/auth/me`, { silent: true });
      if (res.ok) {
        setUser(await res.json());
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadBilling = async () => {
    try {
      const res = await apiFetch(`${API}/tickets/${ticketId}/billing`, { silent: true });
      if (res.ok) setBilling(await res.json());
    } catch (error) {
      console.error('Error loading billing:', error);
    }
  };

  const handleSubscribe = async () => {
    setProcessingPayment(true);
    try {
      const res = await apiFetch(`${API}/payments/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin, ticket_id: ticketId })
      });
      if (res.ok) {
        window.location.href = (await res.json()).url;
        return;
      }
    } catch (error) {
      console.error('Subscription error:', error);
    }
    setProcessingPayment(false);
  };

  const loadTicketData = async () => {
    try {
      const [ticketRes, messagesRes] = await Promise.all([
        apiFetch(`${API}/tickets/${ticketId}`),
        apiFetch(`${API}/messages/${ticketId}?skip=0&limit=100`)
      ]);

      if (ticketRes.ok) setTicket(await ticketRes.json());
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages || data);
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const res = await apiFetch(`${API}/messages/${ticketId}?skip=0&limit=100`, { silent: true });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const res = await apiFetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticketId, content: newMessage })
      });

      if (res.ok) {
        setNewMessage('');
        await loadMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handlePayment = async () => {
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const res = await apiFetch(`${API}/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: ticket.service_id,
          ticket_id: ticketId,
          origin_url: originUrl
        })
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      setProcessingPayment(false);
    }
  };

  const updateTicketStatus = async (newStatus) => {
    if (!user || user.role !== 'admin') return;
    
    setUpdatingStatus(true);
    try {
      const res = await apiFetch(`${API}/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        await loadTicketData();
        toast.success(`Ticket marked as ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      const res = await apiFetch(`${API}/tickets/${ticketId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: resolveFeedback || null })
      });
      if (res.ok) {
        setShowResolveConfirm(false);
        setResolveFeedback('');
        await loadTicketData();
        toast.success('Ticket closed', { description: 'Thanks for confirming! A summary has been saved.' });
      }
    } catch (error) {
      console.error('Resolve error:', error);
    } finally {
      setResolving(false);
    }
  };

  const handleEscalate = async () => {
    if (!window.confirm('Connect with a human technician? Your ticket will be flagged for our team to follow up directly.')) return;
    setEscalating(true);
    try {
      const res = await apiFetch(`${API}/tickets/${ticketId}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Customer requested human support' })
      });
      if (res.ok) {
        await loadTicketData();
        toast.success('Connecting you to a human', { description: "Our team has been notified. They'll reply here shortly." });
      }
    } catch (error) {
      console.error('Escalate error:', error);
    } finally {
      setEscalating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00f0ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#00f0ff] glow">Loading ticket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      
      {/* Header */}
      <nav className="relative z-10 border-b border-[#00f0ff]/30 bg-[#0a0a1f]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-[#00f0ff] hover:text-[#ff00ff] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Ticket Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass rounded-xl p-6 glow-box sticky top-6">
              <h3 className="text-xl font-bold text-[#00f0ff] mb-4">Ticket Details</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-[#00f0ff]/60 text-sm">Service</p>
                  <p className="text-[#00f0ff] font-semibold">{ticket?.service_name}</p>
                </div>
                
                <div>
                  <p className="text-[#00f0ff]/60 text-sm">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${getStatusClassName(ticket?.status)}`}>
                    {ticket?.status.replace('_', ' ').toUpperCase()}
                  </span>
                  {ticket?.escalated_to_human && (
                    <span className="inline-block ml-2 px-3 py-1 rounded-full text-xs font-semibold mt-1 bg-[#ff00ff]/20 text-[#ff00ff] border border-[#ff00ff]" data-testid="escalated-badge">
                      🛟 ESCALATED
                    </span>
                  )}
                </div>

                {/* Customer self-service controls (resolve / escalate) */}
                {user && user.role !== 'admin' && ticket?.status !== 'closed' && (
                  <div className="pt-3 border-t border-[#00f0ff]/30">
                    <p className="text-[#00f0ff]/80 text-xs font-semibold mb-2">Need to wrap up?</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowResolveConfirm(true)}
                        disabled={resolving}
                        className="w-full px-3 py-2 bg-[#00ff00]/20 border border-[#00ff00] text-[#00ff00] text-sm rounded-lg hover:bg-[#00ff00] hover:text-[#0a0a1f] transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                        data-testid="customer-resolve-button"
                      >
                        <CheckCircle2 size={16} />
                        <span>Mark as Resolved</span>
                      </button>
                      {!ticket?.escalated_to_human && (
                        <button
                          onClick={handleEscalate}
                          disabled={escalating}
                          className="w-full px-3 py-2 bg-[#ff00ff]/20 border border-[#ff00ff] text-[#ff00ff] text-sm rounded-lg hover:bg-[#ff00ff] hover:text-white transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                          data-testid="customer-escalate-button"
                        >
                          <LifeBuoy size={16} />
                          <span>{escalating ? 'Connecting…' : 'Talk to a Human'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Resolve confirmation modal */}
                {showResolveConfirm && (
                  <div className="pt-3 border-t border-[#00ff00]/40">
                    <p className="text-[#00ff00] text-sm font-semibold mb-2">All fixed up?</p>
                    <p className="text-[#00f0ff]/70 text-xs mb-2">Optional — leave a quick note about how it went:</p>
                    <textarea
                      value={resolveFeedback}
                      onChange={(e) => setResolveFeedback(e.target.value)}
                      placeholder="e.g., 'The driver update did the trick — thanks!'"
                      className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00ff00]/40 rounded-lg text-[#00f0ff] text-sm focus:border-[#00ff00] focus:outline-none mb-2"
                      rows={3}
                      data-testid="resolve-feedback-input"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleResolve}
                        disabled={resolving}
                        className="flex-1 px-3 py-2 bg-[#00ff00] text-[#0a0a1f] text-sm font-bold rounded-lg hover:bg-[#00cc00] disabled:opacity-50"
                        data-testid="confirm-resolve-button"
                      >
                        {resolving ? 'Closing…' : 'Confirm & Close'}
                      </button>
                      <button
                        onClick={() => { setShowResolveConfirm(false); setResolveFeedback(''); }}
                        className="px-3 py-2 border border-[#00f0ff]/50 text-[#00f0ff] text-sm rounded-lg hover:bg-[#00f0ff]/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Admin Controls for Status */}
                {user && user.role === 'admin' && ticket?.status !== 'closed' && (
                  <div className="pt-3 border-t border-[#00f0ff]/30">
                    <p className="text-[#ff00ff]/80 text-xs font-semibold mb-2">🔐 Admin Actions</p>
                    <div className="space-y-2">
                      {ticket?.status === 'open' && (
                        <button
                          onClick={() => updateTicketStatus('in_progress')}
                          disabled={updatingStatus}
                          className="w-full px-3 py-2 bg-[#ffa500]/20 border border-[#ffa500] text-[#ffa500] text-sm rounded-lg hover:bg-[#ffa500] hover:text-white transition-all disabled:opacity-50"
                        >
                          Mark In Progress
                        </button>
                      )}
                      <button
                        onClick={() => setShowResolveConfirm(true)}
                        disabled={resolving}
                        className="w-full px-3 py-2 bg-[#00ff00]/20 border border-[#00ff00] text-[#00ff00] text-sm rounded-lg hover:bg-[#00ff00] hover:text-[#0a0a1f] transition-all disabled:opacity-50"
                        data-testid="admin-resolve-button"
                      >
                        ✓ Resolve & Close (with AI summary)
                      </button>
                    </div>
                  </div>
                )}
                
                <div>
                  <p className="text-[#00f0ff]/60 text-sm">Priority</p>
                  <span className={`font-semibold ${getPriorityClassName(ticket?.priority)}`}>
                    {ticket?.priority.toUpperCase()}
                  </span>
                </div>
                
                <div>
                  <p className="text-[#00f0ff]/60 text-sm">Created</p>
                  <p className="text-[#00f0ff]">{new Date(ticket?.created_at).toLocaleString()}</p>
                </div>

                <div className="pt-4 border-t border-[#00f0ff]/30">
                  <TicketBillingCard billing={billing} processing={processingPayment} onPay={handlePayment} onSubscribe={handleSubscribe} />
                </div>
              </div>
            </div>
          </div>

          {/* Chat Section */}
          <div className="lg:col-span-2">
            <div className="glass rounded-xl p-6 glow-box">
              <h3 className="text-2xl font-bold text-[#00f0ff] mb-2">{ticket?.title}</h3>
              <p className="text-[#00f0ff]/70 mb-6">{ticket?.description}</p>

              {/* Resolution Summary (shown only when closed) */}
              {ticket?.status === 'closed' && ticket?.resolution_summary && (
                <div className="mb-6 p-4 rounded-xl border-2 border-[#00ff00] bg-[#00ff00]/5" data-testid="resolution-summary-card">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle2 className="text-[#00ff00]" size={20} />
                    <h4 className="text-[#00ff00] font-bold">Resolution Summary</h4>
                    {ticket?.closed_by && (
                      <span className="text-xs text-[#00f0ff]/60">— closed by {ticket.closed_by}</span>
                    )}
                  </div>
                  <p className="text-[#00f0ff]/90 text-sm whitespace-pre-wrap">{ticket.resolution_summary}</p>
                </div>
              )}

              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto" data-testid="messages-container">
                {messages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${msg.message_id}`}
                  >
                    <div className={`max-w-[80%] ${getMessageClassName(msg.sender_type)} p-4`}>
                      <div className="flex items-center space-x-2 mb-2">
                        {msg.sender_type === 'ai' && <Bot size={16} />}
                        {msg.sender_type === 'admin' && <Shield size={16} />}
                        {msg.sender_type === 'user' && <UserIcon size={16} />}
                        <span className="font-semibold text-sm">{msg.sender_name}</span>
                      </div>
                      <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs text-white/60 mt-2">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              {ticket?.status === 'closed' ? (
                <div className="text-center py-4 text-[#00f0ff]/60 text-sm border border-[#00f0ff]/20 rounded-lg" data-testid="ticket-closed-banner">
                  This ticket is closed. If you need more help, please open a new ticket.
                </div>
              ) : (
                <form onSubmit={sendMessage} className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={ticket?.escalated_to_human ? "A human tech will reply here soon…" : "Type your message…"}
                    className="flex-1 px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-6 py-3 btn-primary text-white font-bold rounded-lg disabled:opacity-50 flex items-center space-x-2"
                    data-testid="send-message-button"
                  >
                    <Send size={20} />
                  </button>
                </form>
              )}
            </div>

            {/* Review Card - Show when ticket is resolved or closed */}
            {(ticket?.status === 'resolved' || ticket?.status === 'closed') && (
              <div className="mt-6">
                <ReviewReferralCard
                  ticketId={ticketId}
                  resolutionSummary={ticket?.resolution_summary || ''}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketDetail;